import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { CheckCircle, XCircle, Loader2, Send } from 'lucide-react';

interface Props {
    content: string;
    onConfirm: () => void;
    onRefine: (feedback: string) => void;
    isRefining: boolean;
}

export default function PreAnalysisReview({ content, onConfirm, onRefine, isRefining }: Props) {
    const [showInput, setShowInput] = useState(false);
    const [feedback, setFeedback] = useState('');

    const handleSendRefine = () => {
        if (!feedback.trim()) return;
        onRefine(feedback);
        setFeedback('');
        setShowInput(false);
    };

    return (
        <div className="space-y-4">
            <div className="bg-navy-50 dark:bg-navy-800/50 p-5 rounded-xl border border-navy-200 dark:border-navy-700 prose dark:prose-invert max-w-none text-sm">
                <p className="font-semibold mb-3 text-navy-900 dark:text-navy-100">Resumo Preliminar:</p>
                <div className="text-navy-700 dark:text-navy-300">
                    <ReactMarkdown>{content}</ReactMarkdown>
                </div>
            </div>

            {isRefining ? (
                <div className="flex items-center justify-center space-x-3 py-6">
                    <Loader2 className="h-5 w-5 text-accent-500 animate-spin" />
                    <span className="text-accent-600 dark:text-accent-400 font-medium">
                        Ajustando análise com a IA...
                    </span>
                </div>
            ) : (
                <>
                    {!showInput ? (
                        <div className="flex gap-3 justify-end pt-4 border-t border-navy-200 dark:border-navy-700">
                            <button
                                onClick={() => setShowInput(true)}
                                className="inline-flex items-center px-4 py-2.5 text-navy-700 dark:text-navy-200 bg-navy-100 dark:bg-navy-800 rounded-lg hover:bg-navy-200 dark:hover:bg-navy-700 transition-colors duration-200"
                                type="button"
                            >
                                <XCircle className="h-4 w-4 mr-2" />
                                Discordar
                            </button>
                            <button
                                onClick={onConfirm}
                                className="inline-flex items-center px-4 py-2.5 btn-accent rounded-lg"
                                type="button"
                            >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Concordar
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-3 p-4 bg-navy-100 dark:bg-navy-800 rounded-xl border border-navy-200 dark:border-navy-700 animate-in fade-in slide-in-from-top-2">
                            <label className="block text-sm font-medium text-navy-700 dark:text-navy-200">
                                Indique para a IA o que deve ser alterado:
                            </label>
                            <textarea
                                value={feedback}
                                onChange={(e) => setFeedback(e.target.value)}
                                className="w-full p-3 rounded-lg border border-navy-300 dark:border-navy-600 bg-white dark:bg-navy-900 text-navy-900 dark:text-navy-100 placeholder-navy-400 dark:placeholder-navy-500 focus:border-accent-400 focus:ring-2 focus:ring-accent-400/30 transition-all duration-200"
                                rows={3}
                                placeholder="Ex: O documento trata de licitação, não de contrato..."
                            />
                            <div className="flex gap-2 justify-end">
                                <button
                                    onClick={() => setShowInput(false)}
                                    className="px-4 py-2 text-navy-600 dark:text-navy-300 hover:text-navy-800 dark:hover:text-navy-100 transition-colors duration-200"
                                    type="button"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleSendRefine}
                                    className="inline-flex items-center px-4 py-2 btn-accent rounded-lg disabled:bg-navy-300 disabled:text-navy-500"
                                    type="button"
                                    disabled={!feedback.trim()}
                                >
                                    <Send className="h-4 w-4 mr-2" />
                                    Enviar
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
